import { useMemo } from "react";

import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { useGetMaintenancePlanGroups } from "../queries/maintenance-plan-group";
import { useGetPropertyParts } from "../queries/property-part";
import { formatMaintenancePlanCodeOption } from "../services/maintenance-plan-group";
import { formatPropertyPartLabel } from "../services/property-part";
import { useLocale } from "../stores/locale";
import { recordIdToId } from "../utils/record-id-to-id";
import { Button } from "./button";
import { Input } from "./input";
import { Select } from "./select";

export type MaintenancePlanFormValues = {
  maintenancePlanCode: string;
  propertyPart: string;
  description: string;
  cost: string;
  yearlyFrequency: string;
};

export function MaintenancePlanForm({
  onCancel,
  onChange,
  onSubmit,
  values,
  loading = false,
  disabled = false,
  hasError = false,
}: {
  onCancel: () => void;
  onChange: (values: MaintenancePlanFormValues) => void;
  onSubmit: () => void;
  values: MaintenancePlanFormValues;
  loading?: boolean;
  disabled?: boolean;
  hasError?: boolean;
}) {
  const t = useLocale((state) => state.locale);
  const { data: groups } = useGetMaintenancePlanGroups();
  const { data: propertyParts } = useGetPropertyParts();

  const codeOptions = useMemo(() => {
    if (!groups) return [];
    return groups.flatMap((group) =>
      group.codes.map((code) => formatMaintenancePlanCodeOption(group, code)),
    );
  }, [groups]);

  const propertyPartOptions = useMemo(() => {
    if (!propertyParts) return [];
    return propertyParts.map((part) => ({
      label: formatPropertyPartLabel(part),
      value: recordIdToId(part.id),
    }));
  }, [propertyParts]);

  const isValid = useMemo(() => {
    return (
      values.maintenancePlanCode !== "" &&
      values.propertyPart !== "" &&
      values.description.trim() !== ""
    );
  }, [values]);

  const isDisabled = disabled || loading;

  return (
    <form
      className="flex-1 flex flex-col justify-between border-1 border-gray-300 rounded-xs p-4"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <div className="grid grid-cols-2 gap-2">
        <Select
          disabled={isDisabled}
          label={t.maintenance.code}
          options={codeOptions}
          placeholder={t.select.placeholder}
          value={values.maintenancePlanCode}
          onChange={(value) =>
            onChange({ ...values, maintenancePlanCode: value })
          }
        />
        <Select
          disabled={isDisabled}
          label={t.maintenance.propertyPart}
          options={propertyPartOptions}
          placeholder={t.select.placeholder}
          value={values.propertyPart}
          onChange={(value) => onChange({ ...values, propertyPart: value })}
        />
        <div className="col-span-2">
          <Input
            required
            disabled={isDisabled}
            label={t.maintenance.description}
            type="text"
            value={values.description}
            onChange={(e) =>
              onChange({ ...values, description: e.target.value })
            }
          />
        </div>
        <Input
          disabled={isDisabled}
          label={t.maintenance.cost}
          type="number"
          value={values.cost}
          onChange={(e) => onChange({ ...values, cost: e.target.value })}
        />
        <Input
          disabled={isDisabled}
          label={t.maintenance.yearlyFrequency}
          type="number"
          value={values.yearlyFrequency}
          onChange={(e) =>
            onChange({ ...values, yearlyFrequency: e.target.value })
          }
        />
      </div>
      {hasError && (
        <div className="flex items-center gap-2 text-red-600 mt-4">
          <FontAwesomeIcon icon={faTriangleExclamation} />
          <span>{t.maintenance.error}</span>
        </div>
      )}
      <div className="flex justify-end gap-2 mt-4">
        <Button disabled={isDisabled} type="button" onClick={onCancel}>
          {t.cancel}
        </Button>
        <Button disabled={isDisabled || !isValid} loading={loading} type="submit">
          {t.send.submit}
        </Button>
      </div>
    </form>
  );
}
